import { useState, useCallback, useRef } from 'react';
import { MAX_FILE_SIZE } from '../constants/fileConstants';

/**
 * Custom hook for managing file operations (add, remove, update)
 * 
 * This hook provides file management capabilities with proper state management,
 * error handling, and integration with the existing file content system.
 * It follows SOLID principles and can work with any FileService implementation.
 * 
 * @param {Object} options - Configuration options
 * @param {FileService} options.fileService - File service instance
 * @param {function} options.onFilesChanged - Callback when files list changes
 * @param {number} options.maxFileSize - Maximum file size in bytes (default: 10MB)
 * @param {Array<string>} options.allowedMimeTypes - Allowed MIME types (default: all)
 * @returns {object} Object containing file management functions and state
 */
export const useFileManager = (options = {}) => {
  const {
    fileService = null,
    onFilesChanged = null,
    maxFileSize = MAX_FILE_SIZE,
    allowedMimeTypes = null
  } = options;

  const [files, setFiles] = useState([]);
  const [loadingStates, setLoadingStates] = useState(new Map());
  const [errorStates, setErrorStates] = useState(new Map());
  const pendingOperationsRef = useRef(new Map());

  /**
   * Validates a file before upload
   * @param {File} file - Browser File object
   * @returns {object} Validation result
   */
  const validateFile = useCallback((file) => {
    const errors = [];
    
    if (!file || !(file instanceof File)) {
      errors.push('Invalid file object');
      return { isValid: false, errors };
    }
    
    if (file.size > maxFileSize) {
      errors.push(`File size exceeds maximum allowed size of ${(maxFileSize / 1024 / 1024).toFixed(1)}MB`);
    }
    
    if (allowedMimeTypes && !allowedMimeTypes.includes(file.type)) {
      errors.push(`File type ${file.type} is not allowed`);
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }, [maxFileSize, allowedMimeTypes]);

  /**
   * Converts a File object to base64
   * @param {File} file - Browser File object
   * @returns {Promise<string>} Base64 encoded content
   */
  const fileToBase64 = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data URL prefix
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  /**
   * Adds a new file to the system
   * @param {File} file - Browser File object
   * @param {object} metadata - Additional metadata (optional)
   * @returns {Promise<object>} Created file object
   */
  const addFile = useCallback(async (file, metadata = {}) => {
    if (!fileService) {
      throw new Error('File service not provided');
    }

    // Validate file
    const validation = validateFile(file);
    if (!validation.isValid) {
      throw new Error(`File validation failed: ${validation.errors.join(', ')}`);
    }

    // Check if operation is already in progress for this file
    const operationKey = `add-${file.name}-${file.size}`;
    if (pendingOperationsRef.current.has(operationKey)) {
      return pendingOperationsRef.current.get(operationKey);
    }

    // Set loading state
    setLoadingStates(prev => new Map(prev).set(operationKey, true));
    setErrorStates(prev => new Map(prev).set(operationKey, null));

    const operationPromise = (async () => {
      try {
        // Convert file to base64
        const content = await fileToBase64(file);
        
        // Prepare file data
        const fileData = {
          name: file.name,
          mimeType: file.type,
          size: file.size,
          content,
          file,
          metadata
        };

        // Add file via service
        const newFile = await fileService.addFile(fileData);
        
        // Update local state
        setFiles(prev => {
          const updatedFiles = [...prev, newFile];
          // Notify parent component with updated state
          if (onFilesChanged) {
            onFilesChanged(updatedFiles);
          }
          return updatedFiles;
        });
        
        setLoadingStates(prev => new Map(prev).set(operationKey, false));
        return newFile;
      } catch (err) {
        console.error('Error adding file:', err);
        const errorMessage = err.message || 'Failed to add file';
        setErrorStates(prev => new Map(prev).set(operationKey, errorMessage));
        setLoadingStates(prev => new Map(prev).set(operationKey, false));
        throw err;
      } finally {
        pendingOperationsRef.current.delete(operationKey);
      }
    })();

    pendingOperationsRef.current.set(operationKey, operationPromise);
    return operationPromise;
  }, [fileService, validateFile, fileToBase64]);

  /**
   * Removes a file from the system
   * @param {string} fileId - The ID of the file to remove
   * @returns {Promise<boolean>} True if file was successfully removed
   */
  const deleteFile = useCallback(async (fileId) => {
    if (!fileService) {
      throw new Error('File service not provided');
    }

    if (!fileId) {
      throw new Error('File ID is required');
    }

    // Check if operation is already in progress
    if (pendingOperationsRef.current.has(`remove-${fileId}`)) {
      return pendingOperationsRef.current.get(`remove-${fileId}`);
    }

    // Set loading state
    setLoadingStates(prev => new Map(prev).set(`remove-${fileId}`, true));
    setErrorStates(prev => new Map(prev).set(`remove-${fileId}`, null));

    const operationPromise = (async () => {
      try {
        // Remove file via service
        const success = await fileService.deleteFile(fileId);
        
        // Update local state
        setFiles(prev => {
          const updatedFiles = prev.filter(f => f.id !== fileId);
          // Notify parent component with updated state
          if (onFilesChanged) {
            onFilesChanged(updatedFiles);
          }
          return updatedFiles;
        });
        
        setLoadingStates(prev => new Map(prev).set(`remove-${fileId}`, false));
        return success;
      } catch (err) {
        console.error('Error removing file:', err);
        const errorMessage = err.message || 'Failed to remove file';
        setErrorStates(prev => new Map(prev).set(`remove-${fileId}`, errorMessage));
        setLoadingStates(prev => new Map(prev).set(`remove-${fileId}`, false));
        throw err;
      } finally {
        pendingOperationsRef.current.delete(`remove-${fileId}`);
      }
    })();

    pendingOperationsRef.current.set(`remove-${fileId}`, operationPromise);
    return operationPromise;
  }, [fileService, onFilesChanged]);

  /**
   * Updates file metadata
   * @param {string} fileId - The ID of the file to update
   * @param {object} updates - Object containing fields to update
   * @returns {Promise<object>} Updated file object
   */
  const updateFile = useCallback(async (fileId, updates) => {
    if (!fileService) {
      throw new Error('File service not provided');
    }

    if (!fileId) {
      throw new Error('File ID is required');
    }

    // Check if operation is already in progress
    if (pendingOperationsRef.current.has(`update-${fileId}`)) {
      return pendingOperationsRef.current.get(`update-${fileId}`);
    }

    // Set loading state
    setLoadingStates(prev => new Map(prev).set(`update-${fileId}`, true));
    setErrorStates(prev => new Map(prev).set(`update-${fileId}`, null));

    const operationPromise = (async () => {
      try {
        // Update file via service
        const updatedFile = await fileService.updateFile(fileId, updates);
        
        // Update local state
        setFiles(prev => {
          const updatedFiles = prev.map(f => f.id === fileId ? updatedFile : f);
          // Notify parent component with updated state
          if (onFilesChanged) {
            onFilesChanged(updatedFiles);
          }
          return updatedFiles;
        });
        
        setLoadingStates(prev => new Map(prev).set(`update-${fileId}`, false));
        return updatedFile;
      } catch (err) {
        console.error('Error updating file:', err);
        const errorMessage = err.message || 'Failed to update file';
        setErrorStates(prev => new Map(prev).set(`update-${fileId}`, errorMessage));
        setLoadingStates(prev => new Map(prev).set(`update-${fileId}`, false));
        throw err;
      } finally {
        pendingOperationsRef.current.delete(`update-${fileId}`);
      }
    })();

    pendingOperationsRef.current.set(`update-${fileId}`, operationPromise);
    return operationPromise;
  }, [fileService, onFilesChanged]);

  /**
   * Checks if an operation is loading
   * @param {string} operationKey - Operation identifier
   * @returns {boolean} Whether the operation is loading
   */
  const isLoading = useCallback((operationKey) => {
    return loadingStates.get(operationKey) || false;
  }, [loadingStates]);

  /**
   * Gets error for an operation
   * @param {string} operationKey - Operation identifier
   * @returns {string|null} The error message or null
   */
  const getError = useCallback((operationKey) => {
    return errorStates.get(operationKey) || null;
  }, [errorStates]);

  /**
   * Sets the files list (useful for initialization)
   * @param {Array} newFiles - Array of file objects
   */
  const setFilesList = useCallback((newFiles) => {
    setFiles(newFiles || []);
  }, []);

  /**
   * Clears all errors
   */
  const clearErrors = useCallback(() => {
    setErrorStates(new Map());
  }, []);

  return {
    // State
    files,
    
    // Operations
    addFile,
    deleteFile,
    updateFile,
    
    // Utilities
    validateFile,
    setFilesList,
    clearErrors,
    
    // Status
    isLoading,
    getError
  };
}; 